/**
 * ChatHistoryHelper
 * 
 * Static utility methods for chat history parsing and formatting operations.
 * 
 * Methods:
 * 1. parseChatResponse() - Parse chat response into structured objects
 * 2. generateGuid() - Generate unique identifier for chat sessions
 */

import { v4 as uuidv4 } from 'uuid';

export class ChatHistoryHelper {
  
  /**
   * Parse chat response array into structured response objects
   */
  static parseChatResponse(ChatResponse: any): {
    AgentPayload?: any;
    GetMcode?: any;
    getSnapshotConfiguration?: any;
    GetCustomSnapshotWidget?: any;
    GetQuery?: any;
    getSnapshotOutput?: any;
    getSnapshotLoadData?: any;
    getConfiguration?: any;
    getSnapshotInsights?: any;
    getSnapshotInsightsTrend?: any;
    getInsights?: any;
    getQnA?: any;
    trendline_response?: any;
    response_payload_trendline?: any;
    customization_Properties_trendline?: any;
    response_payload?: any;
    member_responses?: any;
  } {
    const parsedResponses: Record<string, any> = {};

    let targetPayload = (Array.isArray(ChatResponse) && ChatResponse.length === 1) ? ChatResponse[0] : ChatResponse;

    if (typeof targetPayload === 'object' && targetPayload !== null && 
       (targetPayload.snapshot || targetPayload.brand_analysis || targetPayload.brandanalysis || targetPayload.crosstab || targetPayload.final_mcode !== undefined)) {
        // NEW FORMAT: targetPayload is { snapshot: {...}, brand_analysis: {...}, crosstab: {...}, final_mcode: 3 }
        const finalMcode = targetPayload.final_mcode;
        
        let activeMod: any;
        if (finalMcode === 1) activeMod = targetPayload.snapshot;
        else if (finalMcode === 2) activeMod = targetPayload.brand_analysis || targetPayload.brandanalysis;
        else if (finalMcode === 3) activeMod = targetPayload.crosstab;
        
        // Fallback if final_mcode is missing or activeMod wasn't found
        if (!activeMod) {
          activeMod = targetPayload.snapshot || targetPayload.brand_analysis || targetPayload.brandanalysis || targetPayload.crosstab || {};
        }
        if (activeMod.getSnapshotOutput) {
            if (typeof activeMod.getSnapshotOutput === 'object' && activeMod.getSnapshotOutput !== null) {
            } else if (typeof activeMod.getSnapshotOutput === 'string') {
            }
        }
        
        // Helper to safely parse strings that are JSON
        const tryParse = (val: any) => {
          if (typeof val === 'string') {
            try {
              const trimmed = val.trim();
              if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                return JSON.parse(trimmed);
              }
            } catch (e) { }
          }
          return val;
        };

        // Reconstruct AgentPayload for legacy UI logic
        let parsedAgentPayload = tryParse(activeMod.AgentPayload);
        if (!parsedAgentPayload) {
          parsedAgentPayload = {
            mcode: activeMod.mcode,
            promptData: {
              Primary_Brands: activeMod.Primary_Brands,
              Primary_Metrics: activeMod.Primary_Metrics,
              Markets: activeMod.Markets,
              Time_Period: activeMod.Time_Period,
              Filter_Audience: activeMod.Filter_Audience,
              Metric_Filters: activeMod.Metric_Filters,
              snapshot_type: activeMod.snapshot_type,
              Snapshot_Name: activeMod.Snapshot_Name,
              Section: activeMod.Section,
              Widget_ID: activeMod.Widget_ID,
              Widget_Name: activeMod.Widget_Name,
              requested_metrics: activeMod.requested_metrics
            }
          };
        }

        return {
          AgentPayload: parsedAgentPayload,
          GetMcode: tryParse(activeMod.GetMcode) || tryParse(activeMod.mcode) || tryParse(ChatResponse.GetMcode) || tryParse(ChatResponse.mcode),
          getSnapshotConfiguration: tryParse(activeMod.getSnapshotConfiguration) || tryParse(activeMod.getConfiguration) || tryParse(ChatResponse.getSnapshotConfiguration) || tryParse(ChatResponse.getConfiguration),
          GetCustomSnapshotWidget: tryParse(activeMod.GetCustomSnapshotWidget) || tryParse(ChatResponse.GetCustomSnapshotWidget),
          GetQuery: tryParse(activeMod.GetQuery) || tryParse(ChatResponse.GetQuery),
          getSnapshotOutput: tryParse(activeMod.getSnapshotOutput) || tryParse(activeMod.getConfiguration) || tryParse(ChatResponse.getSnapshotOutput),
          getSnapshotLoadData: tryParse(activeMod.getSnapshotLoadData) || tryParse(ChatResponse.getSnapshotLoadData),
          getConfiguration: tryParse(activeMod.getConfiguration) || tryParse(ChatResponse.getConfiguration),
          getSnapshotInsights: tryParse(activeMod.getSnapshotInsights) || tryParse(activeMod.getInsights) || tryParse(targetPayload.getSnapshotInsights),
          getSnapshotInsightsTrend: tryParse(activeMod.getSnapshotInsights_Trend) || tryParse(targetPayload.getSnapshotInsights_Trend),
          getInsights: tryParse(activeMod.getInsights) || tryParse(targetPayload.getInsights),
          getQnA: tryParse(activeMod.getQnA) || tryParse(targetPayload.getQnA),
          trendline_response: tryParse(targetPayload.trendline_response) || tryParse(activeMod.trendline_response),
          response_payload_trendline: tryParse(targetPayload.response_payload_trendline) || tryParse(activeMod.response_payload_trendline),
          customization_Properties_trendline: tryParse(targetPayload.customization_Properties_trendline) || tryParse(activeMod.customization_Properties_trendline),
          response_payload: tryParse(targetPayload.response_payload) || tryParse(activeMod.response_payload),
          member_responses: targetPayload // Inject the whole object as member_responses so toggles work
        };
    }

    ChatResponse.forEach((item: any, index: any) => {
      if (typeof item !== 'object' || item === null) return;

      Object.keys(item).forEach((key) => {
        const value = item[key];
        if (typeof value === 'string') {
          try {
            // Only try to parse if it looks like JSON (starts with { or [)
            const trimmedValue = value.trim();
            if ((trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) ||
              (trimmedValue.startsWith('[') && trimmedValue.endsWith(']'))) {
              parsedResponses[key] = JSON.parse(value);
            } else {
              parsedResponses[key] = value;
            }
          } catch (e) {
            parsedResponses[key] = value; // Keep original value if parsing fails
          }
        } else {
          parsedResponses[key] = value;
        }
      });
    });

    return {
      AgentPayload: parsedResponses['AgentPayload'],
      GetMcode: parsedResponses['GetMcode'],
      getSnapshotConfiguration: parsedResponses['getSnapshotConfiguration'],
      GetCustomSnapshotWidget: parsedResponses['GetCustomSnapshotWidget'],
      GetQuery: parsedResponses['GetQuery'],
      getSnapshotOutput: parsedResponses['getSnapshotOutput'],
      getSnapshotLoadData: parsedResponses['getSnapshotLoadData'],
      getConfiguration: parsedResponses['getConfiguration'],
      getSnapshotInsights: parsedResponses['getSnapshotInsights'],
      getSnapshotInsightsTrend: parsedResponses['getSnapshotInsights_Trend'],
      getInsights: parsedResponses['getInsights'],
      getQnA: parsedResponses['getQnA'],
      trendline_response: parsedResponses['trendline_response'],
      response_payload_trendline: parsedResponses['response_payload_trendline'],
      customization_Properties_trendline: parsedResponses['customization_Properties_trendline'],
      response_payload: parsedResponses['response_payload'],
      member_responses: parsedResponses['member_responses']
    };
  }

  /**
   * Generate unique identifier for chat sessions
   */
  static generateGuid(): string {
    return uuidv4();
  }

  /**
   * Helper method to format chat response entry for storage
   * Used for consistent logging and storage formatting
   */
  static formatChatResponseEntry(key: string, value: any): { [key: string]: string } {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    return { [key]: stringValue };
  }

  /**
   * Helper method to sort chat history by message index
   * Returns sorted array without modifying original
   */
  static sortChatHistoryByIndex(history: any[]): any[] {
    if (!history || history.length === 0) return [];
    
    return [...history].sort((a, b) => {
      const indexA = a.MessageIndex !== undefined ? a.MessageIndex : 0;
      const indexB = b.MessageIndex !== undefined ? b.MessageIndex : 0;
      return indexA - indexB;
    });
  }

  /**
   * Helper method to filter chat list by search tokens
   * Returns filtered chats based on title matching and optionally nested question matching
   */
  static filterChatsBySearch(
    allChats: any[],
    searchText: string,
    nestedSearchText: string = ''
  ): any[] {
    const searchTokens = searchText.toLowerCase().trim().split(/\s+/).filter(token => token.length > 0);
    const nestedTokens = nestedSearchText.toLowerCase().trim().split(/\s+/).filter(token => token.length > 0);
    
    if (searchTokens.length === 0 && nestedTokens.length === 0) {
      return allChats;
    }

    // Step 1: Filter by Primary Search (Chat title)
    let filtered = allChats;
    if (searchTokens.length > 0) {
      filtered = filtered.filter(chat => {
        const title = (chat.ChatTitle || '').toLowerCase();
        return searchTokens.every(token => title.includes(token));
      });
    }

    // Step 2: Nested Filter (Search within history questions of the already filtered chats)
    if (nestedTokens.length > 0) {
      filtered = filtered.map(chat => {
        // Deep clone to avoid modifying original list in memory
        const matchedQuestions = (chat.ChatHistory || []).filter((h: any) => {
          const questionText = (h.Question || '').toLowerCase();
          return nestedTokens.every(token => questionText.includes(token));
        });

        if (matchedQuestions.length > 0) {
          return { ...chat, ChatHistory: matchedQuestions };
        }
        return null;
      }).filter(chat => chat !== null);
    }

    return filtered;
  }

  /**
   * Helper method to map chat list to accordion format grouped by month
   * Returns structured accordion data for UI display
   */
  static mapChatsToAccordions(chats: any[]): any[] {
    const resultGroups: any[] = [];
    const sortedChats = ChatHistoryHelper.sortChatsByDate(chats);
    sortedChats.forEach(chat => {
      const firstQuestion = (chat.ChatHistory || []).find((h: any) => h.MessageIndex === 0);
      const history = (chat.ChatHistory || []).map((message: any) => ({
        ...message,
        AnswerPreview: ChatHistoryHelper.createAnswerPreview(message.QuestionAnswer)
      }));
      const headerTitle = firstQuestion ? firstQuestion.Question : chat.ChatTitle;

      const date = chat.CreatedAt ? new Date(chat.CreatedAt) : new Date();
      const year = date.getFullYear();
      const currentYear = new Date().getFullYear();
      
      let monthKey = date.toLocaleString('default', { month: 'long' });
      
      if (year !== currentYear) {
         monthKey += ` ${year}`;
      }

      let group = resultGroups.find(g => g.month === monthKey);
      if (!group) {
        group = { month: monthKey, isExpanded: true, chats: [] };
        resultGroups.push(group);
      }

      group.chats.push({
        mainQuestion: headerTitle,
        mainQuestionMessageId: firstQuestion?.MessageId || null,
        chatId: chat.ChatId,
        questions: history,
        isExpanded: false
      });
    });

    return resultGroups;
  }

  static createAnswerPreview(questionAnswer: string | undefined): string {
    if (!questionAnswer) return '';

    return questionAnswer
      .replace(/\[\[\[SPLIT\]\]\]/g, ' ')
      .replace(/\\\\n/g, ' ')
      .replace(/\\n/g, ' ')
      .replace(/\/n/g, ' ')
      .replace(/\\\\r/g, ' ')
      .replace(/\\r/g, ' ')
      .replace(/\\t/g, ' ')
      .replace(/\n/g, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/[#*_`~>\[\]()|\\-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Helper method to create chat preview block
   * Returns formatted chat preview object for UI
   */
  static createChatPreviewBlock(
    message: any,
    defaultQuestionAnswer: string,
    defaultWeight: string = 'Total Population',
    defaultBase: string = 'Total Base',
    selectedWeight: string = 'Weight'
  ): any {
    return {
      question: message.Question || '',
      response: message.QuestionAnswer || '',
      messageId: message.MessageId,
      isStarred: message.IsStarred || false,
      isLiked: message.IsLiked ?? null,
      feedbackSaved: (message.IsLiked === true || message.IsLiked === false),
      showLikeInput: false,
      showDislikeInput: false,
      showDeleteConfirm: false,
      likeReason: message.LikeReason || '',
      dislikeReason: message.DislikeReason || '',
      QuestionAnswer: message.QuestionAnswer || defaultQuestionAnswer,
      fullAnswer: null,
      parsedData: null,
      mcode: 0,
      analyticalData: null,
      // Isolated UI State Flags
      isExportspopup: false,
      sampleSizeDropdown: false,
      confidenceLevelDropDown: false,
      referenceLevelDropDown: false,
      baseDropDown: false,
      weightDropDown: false,
      isReference: false,
      // Isolated Analytical Parameters
      isSig: true,
      isIndex: false,
      DefaultSampleSize: 'None',
      SignificanceVsReference: '',
      DefaultConfidenceLevel: '95%',
      DefaultReferenceLevel: 'None',
      selectedWeight: selectedWeight,
      DefaultWeight: defaultWeight,
      DefaultBase: defaultBase,
      isTotalBase: true,
      isAnswerBase: true,
      is_sigtesting: true,
      is_Time_Period_Present: true,
      isTranspose: false,
      isHideNA: false,
      isTotal: false,
      dataDropDown: false,
      DefaultDataDropDown: 'Data',
      isTableClick: true,
      isVerticalStackChartClick: false,
      isVerticalBarChartClick: false,
      isVerticalGroupBarChartClick: false,
      isLineChartClick: false,
      isChartView: true,
      disableStackChart: false,
      disableLineChart: false,
      showStackTooltip: false,
      showLineTooltip: false,
      showBarTooltip: false,
      showMainTooltip: false,
      StackFlag: false,
      displayTable: true,
      isTableClosing: false,
      isChartClosing: false,
      // Isolated Chart Objects
      StackChart: { title: '', Data: [], Properties: [] },
      LineChart: { title: '', Data: [], Properties: [] },
      BarChart: { title: '', Data: [], Properties: [] },
      // Isolated Data Storage
      OutputdataObject: { Row: [], Column: [], RowNesting: [], ColNesting: [], Filter: [], CustomGroup: [], CustomCalculation: [] },
      CrossTabOutPutData: [],
      CrossTabOutPutEntireData: []
    };
  }

  /**
   * Helper method to sort chats by creation date
   * Returns sorted array with most recent first
   */
  static sortChatsByDate(chats: any[]): any[] {
    return [...chats].sort((a, b) => {
      const dateA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
      const dateB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
      return dateB - dateA; // Most recent first
    });
  }
}
